import { reduceDestinationAction } from './destinationActions';
import { createInitialState, STEPS_BY_KIND } from './initialState';
import { reduceMergeTargetAction } from './mergeTargetActions';
import type { OperateWizardAction, OperateWizardState } from './types';

/**
 * Multi-mode wizard for composition-changing operations. The four backend
 * operation types are exposed to the operator as four explicit modes so the
 * intent is always conscious — never inferred from the form's shape:
 *
 *  - `split`            (1 → N empty cages)
 *  - `merge`            (N → 1 cage)
 *  - `split_and_merge`  (1 source split: part absorbs an already-populated
 *                        cage, the rest goes into empty continuation cages)
 *  - `redistribution`   (advanced N × M; the freeform graph wizard)
 *
 * The reducer is mode-aware in two narrow places only: `addDestination`
 * auto-populates `receivesFrom` from the current mode, and the split-and-
 * merge target actions (`setOccupiedTarget` / `setEmptyTarget` /
 * `setSecondSource` / `clearTarget`) orchestrate the source/destination
 * bookkeeping that flow requires. Everything else is generic across modes.
 */

export function operateWizardReducer(
    state: OperateWizardState,
    action: OperateWizardAction
): OperateWizardState {
    switch (action.type) {
        case 'setStep':
            return { ...state, step: action.step };

        case 'setOperationKind': {
            // Picking a mode (or switching to a different one) advances the
            // wizard to the first step of that mode's flow and resets any
            // sources / destinations that came from a previous mode attempt.
            // The initial source is always preserved.
            const initial = state.sources.find((s) => s.fishGroupId === state.initialSourceId);
            const nextSteps = STEPS_BY_KIND[action.kind];
            return {
                ...state,
                operationKind: action.kind,
                step: nextSteps[1] ?? 'kind',
                sources: initial ? [initial] : state.sources,
                destinations: [],
                samTargetMode: null,
                bioscopeMovements: [],
            };
        }

        case 'toggleSource': {
            const exists = state.sources.some((s) => s.fishGroupId === action.source.fishGroupId);
            if (exists) {
                // Removing a source must also strip any inflows referencing it
                // from every destination; otherwise we would carry dangling
                // references and submit an invalid payload.
                const sources = state.sources.filter(
                    (s) => s.fishGroupId !== action.source.fishGroupId
                );
                const destinations = state.destinations.map((dest) => ({
                    ...dest,
                    receivesFrom: dest.receivesFrom.filter(
                        (inflow) => inflow.sourceFishGroupId !== action.source.fishGroupId
                    ),
                }));
                return { ...state, sources, destinations };
            }
            // Adding a source. In merge mode the single destination must
            // receive from every source — sync the inflow eagerly so the
            // payload always reflects the merge semantics.
            const sources = [...state.sources, action.source];
            const destinations =
                state.operationKind === 'merge'
                    ? state.destinations.map((d) => ({
                          ...d,
                          receivesFrom: [
                              ...d.receivesFrom,
                              {
                                  sourceFishGroupId: action.source.fishGroupId,
                                  estimatedFishCount: '',
                              },
                          ],
                      }))
                    : state.destinations;
            return { ...state, sources, destinations };
        }

        case 'addDestination':
        case 'ensureSplitTargets':
        case 'initMergeTargetStub':
        case 'addSplitRemainder':
        case 'removeDestination':
        case 'setDestinationName':
        case 'setDestinationCage':
        case 'setDestinationEstimatedCount':
        case 'setDestinationNotes':
        case 'toggleDestinationInflow':
        case 'setInflowCount':
            return reduceDestinationAction(state, action);

        case 'setOccupiedTarget':
        case 'setEmptyTarget':
        case 'setSecondSource':
        case 'clearTarget':
            return reduceMergeTargetAction(state, action);

        case 'setOperationDate':
            return { ...state, operationDate: action.value };

        case 'setReason':
            return { ...state, reason: action.value };

        case 'setNotes':
            return { ...state, notes: action.value };

        case 'setBioscopeMovementTarget': {
            // Add (or update) the operator's decision to move a specific
            // bioscope into a destination cage. Idempotent in the
            // "already-chosen the same target" case — returns the previous
            // reference so consumers don't re-render needlessly.
            const existing = state.bioscopeMovements.find(
                (m) => m.bioscopeId === action.bioscopeId
            );
            if (existing && existing.targetCageId === action.targetCageId) return state;
            const nextMovements = existing
                ? state.bioscopeMovements.map((m) =>
                      m.bioscopeId === action.bioscopeId
                          ? { ...m, targetCageId: action.targetCageId }
                          : m
                  )
                : [
                      ...state.bioscopeMovements,
                      { bioscopeId: action.bioscopeId, targetCageId: action.targetCageId },
                  ];
            return { ...state, bioscopeMovements: nextMovements };
        }

        case 'clearBioscopeMovement': {
            // Operator reverted to "Stay" for this bioscope. Drops the entry.
            // Idempotent when no entry exists for the bioscope.
            if (!state.bioscopeMovements.some((m) => m.bioscopeId === action.bioscopeId)) {
                return state;
            }
            return {
                ...state,
                bioscopeMovements: state.bioscopeMovements.filter(
                    (m) => m.bioscopeId !== action.bioscopeId
                ),
            };
        }

        case 'reset': {
            // Wipe everything except what identifies the wizard's entry
            // point (initial source + its location). Used when the operator
            // cancels or closes the modal so that reopening starts from
            // scratch rather than resuming a half-filled flow.
            const initial =
                state.sources.find((s) => s.fishGroupId === state.initialSourceId) ??
                state.sources[0];
            if (!initial) return state;
            return createInitialState(initial, state.locationId, action.operationDate ?? null);
        }

        default: {
            // Exhaustiveness — fail loudly if a new action is added without a case.
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}
